import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, ScanLine, Loader2, Briefcase, RefreshCw, TrendingUp, User, Building2 } from 'lucide-react';
import { EMPTY_FORM } from '../components/prestiti/constants';
import { PraticheStats } from '../components/prestiti/PraticheStats';
import { PraticheFilters } from '../components/prestiti/PraticheFilters';
import { ProvvigioniSummary } from '../components/prestiti/ProvvigioniSummary';
import { PraticheTable } from '../components/prestiti/PraticheTable';
import { PraticaForm } from '../components/prestiti/PraticaForm';
import { PraticaDetail } from '../components/prestiti/PraticaDetail';
import { ReportTab } from '../components/prestiti/ReportTab';
import { RinnoviTab } from '../components/prestiti/RinnoviTab';
import { SegnalatoriTab } from '../components/prestiti/SegnalatoriTab';
import { CompagnieTab } from '../components/prestiti/CompagnieTab';
import { CompagnieStatsTab } from '../components/prestiti/CompagnieStatsTab';

export default function Prestiti() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('pratiche');
  const [pratiche, setPratiche] = useState([]);
  const [compagnie, setCompagnie] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStato, setFilterStato] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterBeneficiario, setFilterBeneficiario] = useState('');
  const [provvSummary, setProvvSummary] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editingPratica, setEditingPratica] = useState(null);
  const [selectedPratica, setSelectedPratica] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [report, setReport] = useState(null);
  const ocrNewRef = React.useRef(null);
  const [ocrCreating, setOcrCreating] = useState(false);
  const [segnalatori, setSegnalatori] = useState([]);
  const [segStats, setSegStats] = useState([]);
  const [compagnieStats, setCompagnieStats] = useState([]);
  const [rinnovi, setRinnovi] = useState([]);
  const [rinnoviLoading, setRinnoviLoading] = useState(false);
  const [contactingId, setContactingId] = useState(null);

  const fetchPratiche = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStato) params.append('stato', filterStato);
      if (filterTipo) params.append('tipo', filterTipo);
      if (search) params.append('search', search);
      const r = await api.get(`/prestiti?${params}`);
      setPratiche(r.data);
    } catch { toast.error('Errore caricamento pratiche'); }
    finally { setLoading(false); }
  }, [filterStato, filterTipo, search]);

  const fetchCompagnie = useCallback(async () => {
    try { const r = await api.get('/prestiti/compagnie/list'); setCompagnie(r.data); } catch {}
  }, []);

  const fetchUsers = useCallback(async () => {
    try { const r = await api.get('/users'); setUsers(r.data); } catch {}
  }, []);

  const fetchReport = useCallback(async () => {
    if (!isAdmin) return;
    try { const r = await api.get(`/prestiti/report/provvigioni?anno=${new Date().getFullYear()}`); setReport(r.data); } catch {}
  }, [isAdmin]);

  const fetchProvvSummary = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const now = new Date();
      const r = await api.get(`/prestiti/stats/provvigioni?anno=${now.getFullYear()}`);
      setProvvSummary(r.data);
    } catch {}
  }, [isAdmin]);

  useEffect(() => { fetchPratiche(); fetchCompagnie(); fetchUsers(); fetchProvvSummary(); }, [fetchPratiche, fetchCompagnie, fetchUsers, fetchProvvSummary]);
  useEffect(() => { if (tab === 'report') fetchReport(); }, [tab, fetchReport]);
  useEffect(() => {
    // Fetch segnalatori per il dropdown form
    api.get('/segnalatori').then(r => setSegnalatori(r.data)).catch(() => {});
    if (tab === 'segnalatori') {
      api.get('/segnalatori/stats').then(r => setSegStats(r.data)).catch(() => {});
    }
  }, [tab]);
  useEffect(() => {
    if (tab === 'stats_compagnie') {
      api.get('/prestiti/compagnie-finanziarie/stats').then(r => setCompagnieStats(r.data)).catch(() => {});
    }
  }, [tab]);
  useEffect(() => {
    if (tab === 'rinnovi') {
      setRinnoviLoading(true);
      api.get('/prestiti/rinnovi').then(r => setRinnovi(r.data)).catch(() => {}).finally(() => setRinnoviLoading(false));
    }
  }, [tab]);

  const F = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openNew = () => { setEditingPratica(null); setForm({ ...EMPTY_FORM }); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditingPratica(p);
    const f = {};
    Object.keys(EMPTY_FORM).forEach(k => { f[k] = p[k] !== undefined && p[k] !== null ? String(p[k]) : EMPTY_FORM[k]; });
    f.assicurazione = !!p.assicurazione;
    setForm(f);
    setDialogOpen(true);
  };

  const openDetail = (p) => { setSelectedPratica(p); setDetailOpen(true); };

  const handleSave = async () => {
    if (!form.nome_cliente && !form.cognome_cliente) { toast.error('Inserisci almeno il nome del cliente'); return; }
    const body = { ...form };
    ['importo_richiesto', 'importo_erogato', 'tasso_tan', 'tasso_taeg', 'rata_mensile', 'provvigione_importo', 'provvigione_percentuale'].forEach(k => {
      body[k] = body[k] ? parseFloat(body[k]) : null;
    });
    body.durata_mesi = body.durata_mesi ? parseInt(body.durata_mesi) : null;
    try {
      if (editingPratica) {
        await api.patch(`/prestiti/${editingPratica.id}`, body);
        toast.success('Pratica aggiornata');
      } else {
        await api.post('/prestiti', body);
        toast.success('Pratica creata');
      }
      setDialogOpen(false); fetchPratiche(); fetchReport(); fetchProvvSummary();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa pratica?')) return;
    try { await api.delete(`/prestiti/${id}`); fetchPratiche(); fetchReport(); fetchProvvSummary(); } catch {}
  };

  const handleChangeStato = async (pratica, stato) => {
    try { await api.patch(`/prestiti/${pratica.id}`, { stato }); fetchPratiche(); fetchReport(); fetchProvvSummary(); toast.success('Stato aggiornato'); } catch {}
  };

  const handleUploadDoc = async (praticaId, file, tipo, dataScad) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', tipo);
      fd.append('data_scadenza', dataScad || '');
      const r = await api.post(`/prestiti/${praticaId}/documenti`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Documento caricato');
      const updated = await api.get(`/prestiti/${praticaId}`);
      setSelectedPratica(updated.data);
      fetchPratiche();
      return r.data;
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore upload'); }
    finally { setUploading(false); }
  };

  const handleOCR = async (praticaId, file, tipo) => {
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', tipo);
      const r = await api.post(`/prestiti/${praticaId}/ocr`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success('Dati estratti dal documento');
      return r.data;
    } catch (e) { toast.error('Errore OCR: ' + (e.response?.data?.detail || 'Riprova')); return null; }
    finally { setOcrLoading(false); }
  };

  const reloadSegnalatori = async () => {
    try { const r = await api.get('/segnalatori'); setSegnalatori(r.data); } catch {}
    try { const rs = await api.get('/segnalatori/stats'); setSegStats(rs.data); } catch {}
  };

  const handleContattaRinnovo = async (pratica, metodo) => {
    setContactingId(pratica.id);
    try {
      await api.post(`/prestiti/${pratica.id}/contatta-rinnovo?metodo=${metodo}`);
      if (metodo === 'whatsapp') {
        toast.success(`Messaggio rinnovo inviato a ${pratica.nome_cliente} ${pratica.cognome_cliente}!`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || `Errore contatto ${metodo}`); }
    finally { setContactingId(null); }
  };

  const handleApplyOCR = (data) => {
    const updates = {};
    if (data.nome || data.nome_cliente) updates.nome_cliente = data.nome || data.nome_cliente;
    if (data.cognome || data.cognome_cliente) updates.cognome_cliente = data.cognome || data.cognome_cliente;
    if (data.codice_fiscale) updates.codice_fiscale = data.codice_fiscale;
    if (data.data_nascita) updates.data_nascita = data.data_nascita;
    if (data.compagnia) updates.compagnia = data.compagnia;
    if (data.importo_erogato) updates.importo_erogato = parseFloat(String(data.importo_erogato).replace(/[^\d.,]/g, '').replace(',', '.')) || null;
    if (data.durata_mesi) updates.durata_mesi = parseInt(data.durata_mesi) || null;
    if (data.tasso_tan) updates.tasso_tan = parseFloat(String(data.tasso_tan).replace(',', '.')) || null;
    if (data.tasso_taeg) updates.tasso_taeg = parseFloat(String(data.tasso_taeg).replace(',', '.')) || null;
    if (data.rata_mensile) updates.rata_mensile = parseFloat(String(data.rata_mensile).replace(/[^\d.,]/g, '').replace(',', '.')) || null;
    if (data.data_stipula) updates.data_stipula = data.data_stipula;
    if (data.data_scadenza) updates.data_scadenza = data.data_scadenza;
    if (data.assicurazione) updates.assicurazione = data.assicurazione.toLowerCase().includes('si');
    if (data.numero_contratto) updates.numero_contratto = data.numero_contratto;
    if (Object.keys(updates).length > 0) {
      api.patch(`/prestiti/${selectedPratica.id}`, updates).then(() => {
        toast.success('Dati OCR applicati alla pratica');
        api.get(`/prestiti/${selectedPratica.id}`).then(r => setSelectedPratica(r.data));
        fetchPratiche();
      }).catch(() => toast.error('Errore applicazione dati'));
    }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await api.delete(`/prestiti/${selectedPratica.id}/documenti/${docId}`);
      const updated = await api.get(`/prestiti/${selectedPratica.id}`);
      setSelectedPratica(updated.data);
      toast.success('Documento rimosso');
    } catch { toast.error('Errore rimozione'); }
  };

  const filteredPratiche = filterBeneficiario
    ? pratiche.filter(p => {
        const b = (p.beneficiario_provvigione || '').toLowerCase();
        if (b) return b === filterBeneficiario;
        const derived = (p.segnalato_da || '').trim() ? 'collaboratore' : 'agenzia';
        return derived === filterBeneficiario;
      })
    : pratiche;

  const stats = {
    totale: filteredPratiche.length,
    da_lavorare: filteredPratiche.filter(p => p.stato === 'da_lavorare').length,
    in_istruttoria: filteredPratiche.filter(p => p.stato === 'in_istruttoria').length,
    approvata: filteredPratiche.filter(p => p.stato === 'approvata').length,
    erogata: filteredPratiche.filter(p => p.stato === 'erogata').length,
    rifiutata: filteredPratiche.filter(p => p.stato === 'rifiutata').length,
  };

  return (
    <div className="p-4 md:p-6 min-h-screen" data-testid="prestiti-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="prestiti-title">Prestiti & Cessioni del Quinto</h1>
          <p className="text-sm text-slate-500 mt-0.5">Gestisci pratiche, documenti e provvigioni</p>
        </div>
        <div className="flex gap-2">
          <input type="file" ref={ocrNewRef} className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={async e => {
            if (!e.target.files?.[0]) return;
            setOcrCreating(true);
            try {
              const fd = new FormData();
              fd.append('file', e.target.files[0]);
              fd.append('tipo_doc', 'contratto');
              const r = await api.post('/prestiti/crea-da-documento', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 });
              toast.success(`Pratica creata per ${r.data.pratica.nome_cliente} ${r.data.pratica.cognome_cliente}`);
              fetchPratiche();
              openDetail(r.data.pratica);
            } catch (err) { toast.error(err.response?.data?.detail || 'Errore OCR'); }
            finally { setOcrCreating(false); e.target.value = ''; }
          }} />
          <Button onClick={() => ocrNewRef.current?.click()} size="sm" variant="outline" disabled={ocrCreating}
            className="text-xs" data-testid="crea-da-doc-btn">
            {ocrCreating ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <ScanLine size={14} className="mr-1.5" />}
            Crea da Documento
          </Button>
          <Button onClick={openNew} size="sm" className="bg-slate-800 hover:bg-slate-700 text-white shadow-sm" data-testid="new-pratica-btn">
            <Plus size={16} className="mr-1.5" /> Nuova Pratica
          </Button>
        </div>
      </div>

      {/* Stats */}
      <PraticheStats stats={stats} />

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-white rounded-lg border border-slate-200 p-1 w-fit flex-wrap">
        {[
          { key: 'pratiche', label: 'Pratiche', icon: Briefcase },
          { key: 'rinnovi', label: 'Rinnovi CQ', icon: RefreshCw },
          ...(isAdmin ? [
            { key: 'report', label: 'Report', icon: TrendingUp },
            { key: 'segnalatori', label: 'Segnalatori', icon: User },
            { key: 'stats_compagnie', label: 'Compagnie Stats', icon: Building2 },
            { key: 'compagnie', label: 'Gestisci Compagnie', icon: Building2 },
          ] : []),
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              tab === t.key ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-50'
            }`} data-testid={`tab-${t.key}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {/* ─── TAB: Pratiche ─── */}
      {tab === 'pratiche' && (
        <>
          <PraticheFilters
            search={search} setSearch={setSearch}
            filterStato={filterStato} setFilterStato={setFilterStato}
            filterTipo={filterTipo} setFilterTipo={setFilterTipo}
            filterBeneficiario={filterBeneficiario} setFilterBeneficiario={setFilterBeneficiario}
            isAdmin={isAdmin}
          />
          {isAdmin && provvSummary && <ProvvigioniSummary provvSummary={provvSummary} />}
          <PraticheTable
            pratiche={filteredPratiche} loading={loading} isAdmin={isAdmin}
            onDetail={openDetail} onEdit={openEdit} onDelete={handleDelete} onChangeStato={handleChangeStato}
          />
        </>
      )}

      {/* ─── TAB: Report Provvigioni ─── */}
      {tab === 'report' && isAdmin && <ReportTab report={report} />}

      {/* ─── TAB: Compagnie ─── */}
      {tab === 'compagnie' && isAdmin && <CompagnieTab compagnie={compagnie} onReload={fetchCompagnie} />}

      {/* ─── TAB: Rinnovi CQ ─── */}
      {tab === 'rinnovi' && (
        <RinnoviTab rinnovi={rinnovi} loading={rinnoviLoading} contactingId={contactingId}
          onContatta={handleContattaRinnovo} onDetail={openDetail} />
      )}

      {/* ─── TAB: Segnalatori ─── */}
      {tab === 'segnalatori' && isAdmin && (
        <SegnalatoriTab segnalatori={segnalatori} segStats={segStats} onReload={reloadSegnalatori} />
      )}

      {/* ─── TAB: Stats Compagnie ─── */}
      {tab === 'stats_compagnie' && isAdmin && <CompagnieStatsTab compagnieStats={compagnieStats} />}

      {/* ─── Dialog: Crea/Modifica Pratica ─── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingPratica ? 'Modifica Pratica' : 'Nuova Pratica'}</DialogTitle></DialogHeader>
          <PraticaForm form={form} F={F} isAdmin={isAdmin} compagnie={compagnie} users={users} onSave={handleSave} segnalatori={segnalatori} />
        </DialogContent>
      </Dialog>

      {/* ─── Dialog: Dettaglio Pratica ─── */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {selectedPratica && (
            <PraticaDetail pratica={selectedPratica} isAdmin={isAdmin}
              onUpload={handleUploadDoc} onOCR={handleOCR} uploading={uploading}
              ocrLoading={ocrLoading} onEdit={() => { setDetailOpen(false); openEdit(selectedPratica); }}
              onApplyOCR={handleApplyOCR}
              onDeleteDoc={handleDeleteDoc}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
